import React from 'react';
import {motion} from 'framer-motion';
import {LineChart,Line,XAxis,YAxis,CartesianGrid,Tooltip,ResponsiveContainer,Brush} from 'recharts';
import useDashboardStore from '../store/dashboardStore';
import {formatCurrency,formatUnits} from '../utils/formatters';
import {getDay,nextSunday,isSunday} from 'date-fns';

const EmptyChart=()=> ( 
<div className="h-64 flex items-center justify-center bg-brand-muted2/5 rounded-lg">
<div className="text-center">
<div className="w-16 h-16 mx-auto mb-4 bg-brand-muted2/20 rounded-lg flex items-center justify-center">
<svg className="w-8 h-8 text-brand-muted2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
</svg>
</div>
<h4 className="text-brand-muted text-sm font-medium mb-1">No Data Available</h4>
<p className="text-brand-muted2 text-xs">Upload sales data to see trend analysis</p>
</div>
</div>
);

// Enhanced trend calculation with DoW vs DoW, 12-week WoW, and CUSTOM WoW support
const calculateTrendInfo=(data,metric,preset,compare,dateFrom,dateTo)=> {
if (!data || data.length < 2) {
return {direction: 'flat',change: 0,color: 'text-brand-muted'};
}

// DoW vs DoW comparison logic
if (compare==='DOW' && preset==='DATE' && dateFrom===dateTo && data.some(d=> d.isSelected)) {
console.log('📊 DoW vs DoW trend analysis: comparing selected date vs 30 similar DoWs...');

const selectedDayData=data.find(d=> d.isSelected);
const comparisonDays=data.filter(d=> !d.isSelected && d.isComparison && d[metric] > 0);

if (!selectedDayData || comparisonDays.length===0) {
return {direction: 'flat',change: 0,color: 'text-brand-muted'};
}

// Calculate average of comparison days (30 similar DoWs)
const avgComparison=comparisonDays.reduce((sum,d)=> sum + d[metric],0) / comparisonDays.length;
const selectedValue=selectedDayData[metric];

if (avgComparison > 0) {
const change=((selectedValue - avgComparison) / avgComparison) * 100;
let direction='flat';
let color='text-brand-muted';

if (Math.abs(change) > 5) {// 5% threshold for significance
if (change > 0) {
direction='up';
color='text-brand-good';
} else {
direction='down';
color='text-brand-bad';
}
}

console.log(`📊 DoW trend: ${selectedValue} vs avg ${avgComparison.toFixed(2)} = ${change.toFixed(1)}% (${direction})`);
return {direction,change: Math.abs(change),color};
}
}

// For 12-week WoW comparison (WEEK preset)
if (compare==='WOW' && preset==='WEEK' && data.some(d=> d.isCurrent)) {
console.log('📊 12-week WoW trend analysis: comparing current week vs 12-week average...');

const currentWeekData=data.find(d=> d.isCurrent);
const comparisonWeeks=data.filter(d=> !d.isCurrent && d[metric] > 0);

if (!currentWeekData || comparisonWeeks.length===0) {
return {direction: 'flat',change: 0,color: 'text-brand-muted'};
}

// Calculate average of comparison weeks (12 previous weeks)
const avgComparison=comparisonWeeks.reduce((sum,d)=> sum + d[metric],0) / comparisonWeeks.length;
const currentValue=currentWeekData[metric];

if (avgComparison > 0) {
const change=((currentValue - avgComparison) / avgComparison) * 100;
let direction='flat';
let color='text-brand-muted';

if (Math.abs(change) > 5) {// 5% threshold for significance
if (change > 0) {
direction='up';
color='text-brand-good';
} else {
direction='down';
color='text-brand-bad';
}
}

console.log(`📊 WoW trend: ${currentValue} vs avg ${avgComparison.toFixed(2)} = ${change.toFixed(1)}% (${direction})`);
return {direction,change: Math.abs(change),color};
}
}

// NEW: For CUSTOM WoW comparison with Sunday-based weeks
if (compare==='WOW' && preset==='CUSTOM' && data.some(d=> d.isCurrent)) {
console.log('📊 CUSTOM WoW trend analysis: comparing current period vs 12-week Sunday-based average...');

const currentWeekData=data.find(d=> d.isCurrent);
const comparisonWeeks=data.filter(d=> !d.isCurrent && d.isComparison && d[metric] > 0);

if (!currentWeekData || comparisonWeeks.length===0) {
return {direction: 'flat',change: 0,color: 'text-brand-muted'};
}

// Calculate average of comparison weeks (12 previous Sunday-based weeks)
const avgComparison=comparisonWeeks.reduce((sum,d)=> sum + d[metric],0) / comparisonWeeks.length;
const currentValue=currentWeekData[metric];

if (avgComparison > 0) {
const change=((currentValue - avgComparison) / avgComparison) * 100;
let direction='flat';
let color='text-brand-muted';

if (Math.abs(change) > 5) {// 5% threshold for significance
if (change > 0) {
direction='up';
color='text-brand-good';
} else {
direction='down';
color='text-brand-bad';
}
}

console.log(`📊 CUSTOM WoW trend: ${currentValue} vs avg ${avgComparison.toFixed(2)} = ${change.toFixed(1)}% (${direction})`);
return {direction,change: Math.abs(change),color};
}
}

// For DATE preset with 7-day comparison,use different logic
if (preset==='DATE' && data.some(d=> d.isSelected)) {
console.log('📊 DATE preset trend analysis: comparing selected date vs 7-day average...');

const selectedDayData=data.find(d=> d.isSelected);
const comparisonDays=data.filter(d=> !d.isSelected && d[metric] > 0);

if (!selectedDayData || comparisonDays.length===0) {
return {direction: 'flat',change: 0,color: 'text-brand-muted'};
}

// Calculate average of comparison days
const avgComparison=comparisonDays.reduce((sum,d)=> sum + d[metric],0) / comparisonDays.length;
const selectedValue=selectedDayData[metric];

if (avgComparison > 0) {
const change=((selectedValue - avgComparison) / avgComparison) * 100;
let direction='flat';
let color='text-brand-muted';

if (Math.abs(change) > 5) {// 5% threshold for significance
if (change > 0) {
direction='up';
color='text-brand-good';
} else {
direction='down';
color='text-brand-bad';
}
}

console.log(`📊 DATE trend: ${selectedValue} vs avg ${avgComparison.toFixed(2)} = ${change.toFixed(1)}% (${direction})`);
return {direction,change: Math.abs(change),color};
}
}

// Regular trend calculation for other presets
const validData=data.filter(d=> d[metric] > 0);
if (validData.length < 2) {
return {direction: 'flat',change: 0,color: 'text-brand-muted'};
}

// Use linear regression for trend calculation
const n=validData.length;
let sumX=0,sumY=0,sumXY=0,sumXX=0;

validData.forEach((point,index)=> {
const x=index;
const y=point[metric];
sumX +=x;
sumY +=y;
sumXY +=x * y;
sumXX +=x * x;
});

const slope=(n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);

// Calculate percentage change from first to last trend point
const firstValue=validData[0][metric];
const lastValue=validData[validData.length - 1][metric];
const change=lastValue > 0 ? ((lastValue - firstValue) / firstValue) * 100 : 0;

let direction='flat';
let color='text-brand-muted';

// Only show trend if change is significant (> 3% for trend panel)
if (Math.abs(change) > 3) {
if (change > 0) {
direction='up';
color='text-brand-good';
} else {
direction='down';
color='text-brand-bad';
}
}

return {direction,change: Math.abs(change),color};
};

const TrendPanel=()=> {
const {seriesData,primaryMetric,setPrimaryMetric,compare,monochrome,hasData,preset,dateFrom,dateTo}=useDashboardStore();

const metrics=[
{key: 'revenue',label: 'Revenue',formatter: formatCurrency},
{key: 'profit',label: 'Profit',formatter: formatCurrency},
{key: 'units',label: 'Qty Sold',formatter: formatUnits}
];

const currentMetric=metrics.find(m=> m.key===primaryMetric) || metrics[0];
const hasBaseline=compare !=='NONE';
const lineColor=monochrome ? '#BFBFBF' : '#C9A227';
const baselineColor=monochrome ? '#6E6E6E' : '#BFBFBF';
const selectedDayColor=monochrome ? '#FFFFFF' : '#E74C3C';// Highlight color for selected day
const comparisonColor=monochrome ? '#9E9E9E' : '#9C88FF';// Purple for DoW comparison days
const currentWeekColor=monochrome ? '#FFFFFF' : '#27AE60';// Green for current week

// Calculate trend info for the current metric with enhanced comparison awareness
const trendInfo=hasData && seriesData ? calculateTrendInfo(seriesData,primaryMetric,preset,compare,dateFrom,dateTo) : null;

// Check if this is DATE preset with comparison data
const isDatePresetWithComparison=preset==='DATE' && dateFrom===dateTo && seriesData && seriesData.some(d=> d.isSelected);

// Check if this is DoW vs DoW comparison
const isDowVsDowComparison=compare==='DOW' && preset==='DATE' && dateFrom===dateTo && seriesData && seriesData.some(d=> d.isComparison);

// Check if this is 12-week WoW comparison (WEEK preset)
const is12WeekWowComparison=compare==='WOW' && preset==='WEEK' && seriesData && seriesData.some(d=> d.isCurrent);

// NEW: Check if this is CUSTOM WoW comparison
const isCustomWowComparison=compare==='WOW' && preset==='CUSTOM' && seriesData && seriesData.some(d=> d.isCurrent);

// Get DoW information for display
const getDowInfo=()=> {
if (!isDowVsDowComparison) return null;
const selectedDate=new Date(dateFrom);
const dayOfWeek=getDay(selectedDate);
const dayNames=['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
const comparisonDays=seriesData.filter(d=> d.isComparison);
return {
dayName: dayNames[dayOfWeek],
comparisonCount: comparisonDays.length
};
};

const dowInfo=getDowInfo();

// Get WoW information for display (WEEK preset)
const getWowInfo=()=> {
if (!is12WeekWowComparison) return null;
const currentWeek=seriesData.find(d=> d.isCurrent);
const comparisonWeeks=seriesData.filter(d=> !d.isCurrent);
return {
currentWeekLabel: currentWeek?.weekLabel || 'Current Week',
comparisonCount: comparisonWeeks.length,
totalWeeks: seriesData.length
};
};

const wowInfo=getWowInfo();

// NEW: Get CUSTOM WoW information for display
const getCustomWowInfo=()=> {
if (!isCustomWowComparison) return null;
const currentWeek=seriesData.find(d=> d.isCurrent);
const comparisonWeeks=seriesData.filter(d=> d.isComparison);

// Calculate which week this represents
const endDate=new Date(dateTo);
let referenceSunday;
if (isSunday(endDate)) {
referenceSunday=endDate;
} else {
referenceSunday=nextSunday(endDate);
}

return {
currentWeekLabel: currentWeek?.weekLabel || `Week ${currentWeek?.weekNumber || '?'} ${currentWeek?.weekYear || '?'}`,
comparisonCount: comparisonWeeks.length,
totalWeeks: seriesData.length,
referenceSunday: referenceSunday.toISOString().split('T')[0]
};
};

const customWowInfo=getCustomWowInfo();

return (
<motion.div
className="bg-brand-card rounded-card p-6 shadow-card"
initial={{opacity: 0,scale: 0.9}}
animate={{opacity: 1,scale: 1}}
transition={{duration: 0.16,delay: 0.3}}
>
<div className="flex justify-between items-start mb-4">
<div className="flex-1">
<div className="flex justify-between items-start">
<h3 className="text-lg font-semibold text-brand-fg">
{isDowVsDowComparison && dowInfo 
? `DoW vs DoW Analysis (${dowInfo.dayName})`
: is12WeekWowComparison && wowInfo
? `12-Week WoW Analysis (${wowInfo.currentWeekLabel})`
: isCustomWowComparison && customWowInfo
? `CUSTOM WoW Analysis (${customWowInfo.currentWeekLabel})`
: isDatePresetWithComparison
? '7-Day Trend Analysis'
: 'Trend Analysis'
}
</h3>
{/* Trend Indicator in top-right corner */}
{trendInfo && trendInfo.direction !=='flat' && (
<div className={`flex items-center space-x-1 ${trendInfo.color}`}>
<span className="text-sm">
{trendInfo.direction==='up' ? '↗' : '↘'}
</span>
<span className="text-sm font-medium">
{trendInfo.change.toFixed(0)}%
</span>
</div>
)}
</div>
{/* Enhanced subtitle for different comparison modes */}
<p className="text-brand-muted text-sm mt-1">
{isDowVsDowComparison && dowInfo
? `${currentMetric.label} for selected ${dowInfo.dayName} vs ${dowInfo.comparisonCount} previous ${dowInfo.dayName}s`
: is12WeekWowComparison && wowInfo
? `${currentMetric.label} for ${wowInfo.currentWeekLabel} vs ${wowInfo.comparisonCount} previous weeks`
: isCustomWowComparison && customWowInfo
? `${currentMetric.label} for ${customWowInfo.currentWeekLabel} vs ${customWowInfo.comparisonCount} Sunday-based weeks`
: isDatePresetWithComparison
? `${currentMetric.label} for selected date vs 7-day average`
: `${currentMetric.label} performance over time`
}
{trendInfo && trendInfo.direction !=='flat' && (
<span className={`ml-2 ${trendInfo.color}`}>
• {trendInfo.direction==='up' ? 'Above' : 'Below'} {
isDowVsDowComparison ? 'DoW average' :
is12WeekWowComparison ? '12-week average' :
isCustomWowComparison ? 'Sunday-week average' :
isDatePresetWithComparison ? 'average' : 'trend'
}
</span>
)}
</p>
</div>
</div>

{/* Metric Selector */}
<div className="flex justify-end mb-4">
<div className="flex bg-brand-muted2/10 rounded-button p-1">
{metrics.map((metric)=> (
<motion.button
key={metric.key}
onClick={()=> setPrimaryMetric(metric.key)}
className={`px-3 py-1 rounded-lg text-sm transition-colors ${
primaryMetric===metric.key
? 'bg-brand-accent text-brand-bg'
: 'text-brand-muted hover:text-brand-fg'
}`}
whileHover={{scale: 1.02}}
whileTap={{scale: 0.98}}
>
{metric.label}
</motion.button>
))}
</div>
</div>

{!hasData || !seriesData || seriesData.length===0 ? (
<EmptyChart />
) : (
<div className="h-64 relative">
<ResponsiveContainer width="100%" height="100%">
<LineChart
data={seriesData}
margin={{top: 5,right: 30,left: 20,bottom: 5}}
>
<CartesianGrid strokeDasharray="3 3" stroke="#6E6E6E" opacity={0.3} />
<XAxis
dataKey="date"
stroke="#BFBFBF"
fontSize={12}
tickFormatter={(value)=> {
const date=new Date(value);
if (isDowVsDowComparison) {
// Show abbreviated dates for DoW comparison
return date.toLocaleDateString('en-US',{month: 'short',day: 'numeric'});
} else if (is12WeekWowComparison || isCustomWowComparison) {
// Show week labels for WoW comparison
const dataPoint=seriesData.find(d=> d.date===value);
return dataPoint?.weekLabel ? `W${dataPoint.weekNumber}` : date.toLocaleDateString('en-US',{month: 'short',day: 'numeric'});
} else if (isDatePresetWithComparison) {
// Show day names for 7-day view
return date.toLocaleDateString('en-US',{weekday: 'short'});
}
return date.toLocaleDateString('en-US',{month: 'short',day: 'numeric'});
}}
/>
<YAxis stroke="#BFBFBF" fontSize={12} tickFormatter={currentMetric.formatter} />
<Tooltip
contentStyle={{
backgroundColor: '#111111',
border: '1px solid #6E6E6E',
borderRadius: '8px',
color: '#FFFFFF'
}}
formatter={(value,name)=> [
currentMetric.formatter(value),
name
]}
labelFormatter={(label)=> {
const date=new Date(label);
const dataPoint=seriesData.find(d=> d.date===label);
const isSelected=dataPoint?.isSelected;
const isComparison=dataPoint?.isComparison;
const isCurrent=dataPoint?.isCurrent;
const weekLabel=dataPoint?.weekLabel;

if (isCurrent && weekLabel) {
return `📅 Current Week: ${weekLabel}`;
} else if (isSelected) {
return `📅 Selected: ${date.toLocaleDateString('en-US',{
weekday: 'long',
month: 'short',
day: 'numeric',
year: 'numeric'
})}`;
} else if (isComparison && dowInfo) {
return `📊 Comparison ${dowInfo.dayName}: ${date.toLocaleDateString('en-US',{
weekday: 'long',
month: 'short',
day: 'numeric',
year: 'numeric'
})}`;
} else if (isComparison && (isCustomWowComparison || is12WeekWowComparison)) {
return `📊 ${weekLabel}: ${date.toLocaleDateString('en-US',{
month: 'short',
day: 'numeric',
year: 'numeric'
})}`;
} else if (weekLabel) {
return `📊 ${weekLabel}: ${date.toLocaleDateString('en-US',{
month: 'short',
day: 'numeric',
year: 'numeric'
})}`;
}

return date.toLocaleDateString('en-US',{
weekday: 'long',
month: 'short',
day: 'numeric',
year: 'numeric'
});
}}
/>
{/* Main trend line */}
<Line
type="monotone"
dataKey={primaryMetric}
stroke={lineColor}
strokeWidth={3}
dot={(props)=> {
const isSelected=props.payload?.isSelected;
const isComparison=props.payload?.isComparison;
const isCurrent=props.payload?.isCurrent;

if (isCurrent) {
return (
<circle
cx={props.cx}
cy={props.cy}
r={8}
fill={currentWeekColor}
stroke="#FFFFFF"
strokeWidth={3}
/>
);
} else if (isSelected) {
return (
<circle
cx={props.cx}
cy={props.cy}
r={8}
fill={selectedDayColor}
stroke="#FFFFFF"
strokeWidth={3}
/>
);
} else if (isComparison) {
return (
<circle
cx={props.cx}
cy={props.cy}
r={3}
fill={comparisonColor}
stroke={comparisonColor}
strokeWidth={1}
opacity={0.7}
/>
);
}

return (
<circle
cx={props.cx}
cy={props.cy}
r={4}
fill={lineColor}
stroke={lineColor}
strokeWidth={2}
/>
);
}}
activeDot={{r: 6,stroke: lineColor,strokeWidth: 2}}
/>
{hasBaseline && (
<Line
type="monotone"
dataKey="baseline"
stroke={baselineColor}
strokeWidth={2}
strokeDasharray="5 5"
dot={false}
opacity={0.7}
/>
)}
{/* Only show brush for regular time series (not for special comparison modes) */}
{!isDatePresetWithComparison && !isDowVsDowComparison && !is12WeekWowComparison && !isCustomWowComparison && (
<Brush
dataKey="date"
height={30}
stroke={lineColor}
fill="rgba(201,162,39,0.1)"
/>
)}
</LineChart>
</ResponsiveContainer>

{/* Interactive hint overlay - enhanced for different comparison modes */}
{trendInfo && trendInfo.direction !=='flat' && (
<div className="absolute top-2 right-2 opacity-0 hover:opacity-100 transition-opacity duration-200 pointer-events-none">
<div className="bg-brand-card/90 text-brand-accent text-xs px-2 py-1 rounded border border-brand-accent/20">
{isDowVsDowComparison && dowInfo
? `${trendInfo.direction==='up' ? 'Above' : 'Below'} ${dowInfo.dayName} average`
: is12WeekWowComparison && wowInfo
? `${trendInfo.direction==='up' ? 'Above' : 'Below'} 12-week average`
: isCustomWowComparison && customWowInfo
? `${trendInfo.direction==='up' ? 'Above' : 'Below'} Sunday-week average`
: isDatePresetWithComparison
? `${trendInfo.direction==='up' ? 'Above' : 'Below'} 7-day average`
: `${trendInfo.direction==='up' ? 'Upward' : 'Downward'} trend detected`
}
</div>
</div>
)}
</div>
)}

{/* Enhanced Trend Legend for different comparison modes */}
{hasData && seriesData && seriesData.length > 0 && (
<motion.div
className="mt-4 p-3 bg-brand-muted2/10 rounded-lg"
initial={{opacity: 0,y: 10}}
animate={{opacity: 1,y: 0}}
transition={{delay: 0.4}}
>
<div className="flex items-center justify-between text-xs">
<div className="flex items-center space-x-4">
<div className="flex items-center space-x-1">
<div className="w-3 h-0.5 bg-brand-accent rounded-full"></div>
<span className="text-brand-muted">{currentMetric.label}</span>
</div>
{(is12WeekWowComparison || isCustomWowComparison) && (
<div className="flex items-center space-x-1">
<div className="w-3 h-3 rounded-full bg-brand-good border-2 border-white"></div>
<span className="text-brand-muted">Current Week</span>
</div>
)}
{isDowVsDowComparison && (
<div className="flex items-center space-x-1">
<div className="w-3 h-3 rounded-full bg-brand-bad border-2 border-white"></div>
<span className="text-brand-muted">Selected {dowInfo?.dayName}</span>
</div>
)}
{(isDowVsDowComparison || isCustomWowComparison) && (
<div className="flex items-center space-x-1">
<div className="w-3 h-3 rounded-full" style={{backgroundColor: comparisonColor}} opacity="0.7"></div>
<span className="text-brand-muted">
{isDowVsDowComparison 
? `Comparison ${dowInfo?.dayName}s`
: 'Sunday Weeks'
}
</span>
</div>
)}
{isDatePresetWithComparison && !isDowVsDowComparison && (
<div className="flex items-center space-x-1">
<div className="w-3 h-3 rounded-full bg-brand-bad border-2 border-white"></div>
<span className="text-brand-muted">Selected Day</span>
</div>
)}
{hasBaseline && (
<div className="flex items-center space-x-1">
<div className="w-3 h-0.5 bg-brand-muted2 rounded-full opacity-70" style={{backgroundImage: 'repeating-linear-gradient(to right,#6E6E6E 0,#6E6E6E 2px,transparent 2px,transparent 4px)'}}></div>
<span className="text-brand-muted2">Baseline</span>
</div>
)}
</div>
{/* Enhanced Trend Summary for different comparison modes */}
{trendInfo && (
<div className="flex items-center space-x-4">
{trendInfo.direction !=='flat' ? (
<div className="flex items-center space-x-1">
<span className={`${trendInfo.color}`}>
{trendInfo.direction==='up' ? '↗' : '↘'}
</span>
<span className="text-brand-muted">
{isDowVsDowComparison && dowInfo
? `${trendInfo.change.toFixed(0)}% vs ${dowInfo.dayName} avg`
: is12WeekWowComparison && wowInfo
? `${trendInfo.change.toFixed(0)}% vs 12-week avg`
: isCustomWowComparison && customWowInfo
? `${trendInfo.change.toFixed(0)}% vs Sunday-week avg`
: isDatePresetWithComparison
? `${trendInfo.change.toFixed(0)}% vs avg`
: `${trendInfo.direction==='up' ? 'Rising' : 'Declining'} ${trendInfo.change.toFixed(0)}%`
}
</span>
</div>
) : (
<div className="flex items-center space-x-1">
<span className="text-brand-muted2">—</span>
<span className="text-brand-muted2">
{isDowVsDowComparison 
? 'Near DoW average'
: is12WeekWowComparison 
? 'Near 12-week average'
: isCustomWowComparison 
? 'Near Sunday-week average'
: isDatePresetWithComparison 
? 'Near average' 
: 'Stable trend'
}
</span>
</div>
)}
<span className="text-brand-muted2">•</span>
<span className="text-brand-muted">
{seriesData.filter(d=> d[primaryMetric] > 0).length} data points
{(is12WeekWowComparison || isCustomWowComparison) && wowInfo ? ` (${wowInfo.totalWeeks || customWowInfo?.totalWeeks} weeks)` : ''}
</span>
</div>
)}
</div>
</motion.div>
)}
</motion.div>
);
};

export default TrendPanel;